<template>
  <div>
    <CRow class="justify-content-between mb-3">
      <CCol sm="auto"
        ><div class="ui-type-display-lg">
          {{ $t("user.profile.families.title") }}
        </div>
      </CCol>
      <CCol sm="auto">
        <CButton color="primary" @click="onAddEmail()">
          {{ $t("user.profile.families.add_btn") }}
        </CButton>
      </CCol>
    </CRow>

    <CRow>
      <CCol sm="auto">
        <p>
          {{ $t("user.profile.families.intro") }}
        </p></CCol
      >
    </CRow>

    <CCard v-if="familyData.length > 0">
      <CCardBody>
        <CAlert v-if="error.hasGeneralErrors()" color="danger" class="mb-3">
          {{ error.general().join(" ") }}
        </CAlert>
        <Header
          :data="[
            $t('models.family.name'),
            $t('models.family.f_type'),
            $t('models.family.role'),
            $t('models.family.ts_created'),
            $t('models.family.favorite'),
            'Actions',
          ]"
        />
        <Row
          v-for="(item, index) in familyData"
          :key="index"
          :data="[
            { data: item.name, type: 'text' },
            { data: item.org_type, type: 'text' },
            { data: item.role_type, type: 'text' },
            { data: item.created_date, type: 'text' },
            { data: item.is_favorite, type: 'boolean' },
            { data: '', type: 'actions' },
          ]"
          :border="true"
        >
          <template #actions>
            <div class="d-flex justify-content-end" v-if="!item.is_favorite">
              <dropdown-menu :items="getActions(item)" :select="onActionSelect">
                <font-awesome-icon icon="ellipsis-h" />
              </dropdown-menu>
            </div>
          </template>
        </Row>
      </CCardBody>
    </CCard>

    <CModal :show="modal.show" :closeOnBackdrop="false" :centered="true">
      <template #header>
        <h5 class="modal-title">
          {{ $t("user.profile.families.modal.title") }}
        </h5>
      </template>

      <CAlert v-if="modal.error.hasGeneralErrors()" color="danger" class="mb-3">
        {{ modal.error.general().join(" ") }}
      </CAlert>

      <form action="" @submit.prevent="addBill">
        <CInput
          v-model="modal.form.name"
          :label="$t('models.family.name')"
          type="text"
          :isValid="modal.error.isValidField('name')"
          :invalidFeedback="modal.error.fieldError('name')"
          required
        />

        <CSelect
          :label="$t('models.family.f_type')"
          :options="familyTypeOptions"
          :value.sync="modal.form.type"
          :isValid="modal.error.isValidField('f_type')"
          :invalidFeedback="modal.error.fieldError('f_type')"
          custom
        />
      </form>

      <template #footer>
        <CButton
          color="primary"
          variant="outline"
          @click.prevent="closeModal"
          >{{ $t("common.editor.cancel") }}</CButton
        >
        <vue-ladda
          :loading="modal.loading"
          data-style="zoom-in"
          button-class="btn btn-primary px-4"
          @click.prevent="addBill"
          >{{ $t("common.editor.confirm") }}</vue-ladda
        >
      </template>
    </CModal>
  </div>
</template>

<script>
import Vue from "vue";
import {
  getOptionLabel,
  isValidDate,
  formatDate,
} from "../../../helpers/common";
import errorResponse from "../../../helpers/error";
import {
  getFamilyTypeOptions,
  getRoleTypeOptions,
} from "../../../helpers/options";
import {
  apiGet,
  apiPost,
  apiDelete,
  apiPatch,
} from "../../../helpers/apiHelper";
import Header from "../../../common/table/Header.vue";
import Row from "../../../common/table/Row.vue";
import { forEach } from "lodash";

export default Vue.extend({
  name: "Family",

  components: { Header, Row },

  data() {
    return {
      familyData: [],
      error: errorResponse(),
      modal: {
        show: false,
        loading: false,
        error: errorResponse(),
        form: {
          name: "",
          type: getFamilyTypeOptions(this)[0].value,
        },
      },
    };
  },
  computed: {
    familyTypeOptions() {
      return getFamilyTypeOptions(this);
    },
  },

  methods: {
    getActions(item) {
      const isActive =
        item.id === this.$store.state.connections.current.family.id;
      return [
        {
          id: "DELETE",
          item: item,
          text: this.$t("user.profile.families.delete"),
          disabled: isActive,
        },
        {
          id: "FAVORITE",
          item: item,
          text: this.$t("user.profile.families.favorite"),
          disabled: item.is_favorite,
        },
      ];
    },

    onActionSelect(action) {
      if (action.item.id === "DELETE") {
        this.onRemove(action.item);
      } else if (action.item.id === "FAVORITE") {
        this.onFavorite(action.item);
      }
    },

    onAddEmail() {
      this.modal.error.reset();
      this.modal.form.name = "";
      this.modal.form.type = this.familyTypeOptions[0].value;
      this.modal.show = true;
    },

    closeModal() {
      this.modal.show = false;
    },

    async addBill() {
      const requestParams = {
        name: this.modal.form.name,
        f_type: this.modal.form.type,
      };
      this.modal.error.reset();

      const response = await apiPost("auth_family", requestParams);
      if (response && response.status === 201) {
        this.$store.dispatch("toaster/add", {
          title: "OK!",
          text: this.$t("user.profile.families.add_success"),
          color: "success",
          autohide: true,
        });
        this.modal.show = false;
        await this.fetchData();
      } else if (response && response.errors) {
        this.error.set(response.errors);
      } else {
        console.error("Unexpected response:", response);
      }
    },

    onRemove(data) {
      const item = data.item;
      this.removeEmail(item.id);
    },

    onFavorite(data) {
      const item = data.item;
      this.favoriteFamily(item.id, item.luc_id);
    },

    async removeEmail(id) {
      this.error.reset();

      const response = await apiDelete("auth_family", id);
      if (response && response.status === 200) {
        this.$store.dispatch("toaster/add", {
          title: "OK!",
          text: this.$t("user.profile.families.delete_success"),
          color: "success",
          autohide: true,
        });
        await this.fetchData();
      } else if (response && response.errors) {
        this.error.set(response.errors);
      } else {
        console.error("Unexpected response:", response);
      }
    },

    async favoriteFamily(id, luc_id) {
      this.error.reset();
      const params = {
        id: luc_id,
        favorite: true,
      };

      const response = await apiPatch(
        "auth_family_connection",
        luc_id,
        params,
        [id]
      );
      if (response && response.status === 200) {
        this.$store.dispatch("toaster/add", {
          title: "OK!",
          text: this.$t("user.profile.families.favorite_success"),
          color: "success",
          autohide: true,
        });
        await this.fetchData();
      } else if (response && response.errors) {
        this.error.set(response.errors);
      } else {
        console.error("Unexpected response:", response);
      }
    },

    async fetchData() {
      const response = await apiGet("auth_family");
      if (response && response.data) {
        console.log("family data", response.data.payload.Items);
        // build structred family data array
        forEach(response.data.payload.Items, (item) => {
          if (item.role) {
            // organisation
            item.org_type = getOptionLabel(
              item.f_type,
              getFamilyTypeOptions(this)
            );
            // role
            item.role_type = getOptionLabel(
              item.role.r_type,
              getRoleTypeOptions(this)
            );
          }

          // created date format
          const date = new Date(item.ts_created);
          isValidDate(date)
            ? (item.created_date = formatDate(date, "dd.MM.yyyy - HH:mm"))
            : (item.created_date = "--");
        });
        this.familyData = response.data.payload.Items;
      }
    },
  },
  async created() {
    await this.fetchData();
  },
});
</script>
